/// <reference types="web-bluetooth" />

import { EventEmitter, WebPrinterEventCallback } from './event-emitter';
import { CallbackQueue } from './callback-queue';

const PRINTER_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';
const PRINTER_CHARACTERISTIC_UUID = '00002af1-0000-1000-8000-00805f9b34fb';

const ALL_NAME_PREFIX_FILTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  .split('')
  .map(letter => ({ namePrefix: letter }));

export class WebBluetoothReceiptPrinter {
  #emitter = new EventEmitter();
  #queue = new CallbackQueue();
  #device: BluetoothDevice | null = null;
  #characteristic = null;

  get type() {
    return 'bluetooth';
  }
  get device() {
    return this.#device;
  }

  async connect() {
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: ALL_NAME_PREFIX_FILTERS,
        optionalServices: [PRINTER_SERVICE_UUID],
      });

      if (device) {
        await this.open(device);
      }
    } catch (error) {
      console.log('Could not connect! ' + error);
    }
  }

  async reconnect(previousDevice: { id: string }) {
    if (!navigator.bluetooth.getDevices) {
      return;
    }
    try {
      const devices = await navigator.bluetooth.getDevices();

      const device = devices.find(currentDevice => currentDevice.id == previousDevice.id);

      if (device) {
        await this.open(device);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async open(device: BluetoothDevice) {
    let server: BluetoothRemoteGATTServer;

    try {
      server = await device.gatt.connect();
    } catch (error) {
      console.log(error);
    }
    if (!server) {
      return;
    }
    this.#device = device;

    try {
      const service = await server.getPrimaryService(PRINTER_SERVICE_UUID);
      this.#characteristic = await service.getCharacteristic(PRINTER_CHARACTERISTIC_UUID);
    } catch (error) {
      console.log(error);
      this.#device = null;
      this.#characteristic = null;
      return;
    }

    this.#emitter.emit('connected', {
      type: 'bluetooth',
      name: this.#device.name,
      id: this.#device.id,
    });
    navigator.bluetooth.addEventListener('disconnect', event => {
      if (this.#device == event['device']) {
        this.#emitter.emit('disconnected');
      }
    });
    this.#device.addEventListener('gattserverdisconnected', () => {
      console.info('GATT SERVER DISCONNECTED');

      this.#emitter.emit('disconnected');
    });
  }

  async disconnect() {
    if (!this.#device) {
      return;
    }

    await this.#device.gatt.disconnect();

    this.#device = null;
    this.#characteristic = null;

    this.#emitter.emit('disconnected');
  }

  print(command) {
    return new Promise<void>(resolve => {
      const maxLength = 100;
      const chunks = Math.ceil(command.length / maxLength);

      if (chunks === 1) {
        const data = command;

        this.#queue.add(() => this.#characteristic.writeValue(data));
      } else {
        for (let i = 0; i < chunks; i++) {
          const byteOffset = i * maxLength;
          const length = Math.min(command.length, byteOffset + maxLength);
          const data = command.slice(byteOffset, length);

          this.#queue.add(() => this.#characteristic.writeValue(data));
        }
      }

      this.#queue.add(() => resolve());
    });
  }

  addEventListener(event: string, callback: WebPrinterEventCallback) {
    this.#emitter.on(event, callback);
  }
  clearEventListeners() {
    this.#emitter.clearAll();
  }
}
